import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { API } from "../../config";
import styles from "./Groups.module.css";
import usersStyles from "../Users/Users.module.css";
import { AiOutlinePlus, AiOutlineDown, AiOutlineUp } from "react-icons/ai";
import { RiDeleteBin6Fill } from "react-icons/ri";
import { FaPencilAlt } from "react-icons/fa";
import { CreateGroupDialog } from "./CreateGroupDialog";
import { GroupDeleteConfirmation } from "./GroupDeleteConfirmation";
import { useNavigate } from "react-router-dom";
import { SearchInput } from "../ProductListing/Filters";

export const Groups = () => {
  const [showDialog, setShowDialog] = useState(false);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [editingGroup, setEditingGroup] = useState(null);

  const createGroup = async (name, usersForGroup) => {
    try {
      const response = await axios.post(`${API}/Admin/groups`, {
        groupName: name,
        users: usersForGroup?.map((user) => ({
          id: user._id,
          designation: user.designation,
        })),
      });
      getGroups();

      if (response.status === 201 || response.status === 200) {
        toast.success("Group created successfully");
      } else {
        toast.error("Failed to create group");
      }
    } catch (error) {
      console.log(error);
      toast.error("Failed to create group");
      throw error;
    }
  };

  const updateGroup = async (groupId, groupName, updatedUsers) => {
    try {
      const response = await axios.put(`${API}/Admin/groups/${groupId}`, {
        groupName: groupName,
        users: updatedUsers?.map((user) => ({
          id: user._id || user.id,
          designation: user.designation,
        })),
      });
      getGroups();
      if (response.status === 200 || response.status === 201) {
        toast.success("Group updated successfully");
      } else {
        toast.error("Failed to update group");
      }
    } catch (error) {
      console.log(error);
      toast.error("Failed to update group");
    }
  };

  const deleteGroup = async (groupId) => {
    try {
      await axios.delete(`${API}/Admin/groups/${groupId}`);
      getGroups();
    } catch (error) {
      console.log(error);
      toast.error("Failed to delete group");
    }
  };

  const getGroups = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/Admin/groups`, {
        params: {
          search: debouncedSearch,
        },
      });
      const data = response.data?.userGroups || response.data || [];
      setGroups(Array.isArray(data) ? data : []);
    } catch (error) {
      console.log(error);
      toast.error("Failed to load groups");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGroup = async (groupName, usersForGroup) => {
    try {
      await createGroup(groupName, usersForGroup);
      setShowDialog(false);
      setEditingGroup(null);
    } catch {
      // errors are already handled in createGroup
    }
  };

  const handleUpdateGroup = async (groupId, groupName, usersForGroup) => {
    try {
      await updateGroup(groupId, groupName, usersForGroup);
      setShowDialog(false);
      setEditingGroup(null);
    } catch {
      // errors handled in updateGroup
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    getGroups();
  }, [debouncedSearch]);

  const closeDialog = () => {
    setShowDialog(false);
    setEditingGroup(null);
  };

  const openCreateDialog = () => {
    setEditingGroup(null);
    setShowDialog(true);
  };

  const openEditDialog = (group) => {
    setEditingGroup(group);
    setShowDialog(true);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Groups</h1>
      </div>

      <button
        type="button"
        className={styles.createButton}
        onClick={openCreateDialog}
      >
        <AiOutlinePlus /> Create Group
      </button>

      <SearchInput
        value={search}
        onChange={setSearch}
        placeholder="Search groups"
      />

      <div className={styles.groupsList}>
        {loading ? (
          <div className={styles.groupsEmpty}>Loading groups...</div>
        ) : !groups?.length ? (
          <div className={styles.groupsEmpty}>No groups created yet</div>
        ) : (
          groups.map((group) => (
            <GroupCollapse
              key={group._id}
              group={group}
              onDeleteGroup={deleteGroup}
              onEditGroup={openEditDialog}
            />
          ))
        )}
      </div>

      <CreateGroupDialog
        show={showDialog}
        onClose={closeDialog}
        mode={editingGroup ? "edit" : "create"}
        initialGroup={editingGroup}
        onSave={handleSaveGroup}
        onUpdate={handleUpdateGroup}
      />
    </div>
  );
};

const GroupCollapse = ({ group, onDeleteGroup, onEditGroup }) => {
  const [open, setOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const navigate = useNavigate();
  const users = group.users || [];

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  return (
    <div className={styles.groupItem}>
      <div className={styles.groupHeaderRow}>
        <button
          type="button"
          className={styles.groupHeader}
          onClick={() => setOpen((prev) => !prev)}
        >
          <div className={styles.groupHeaderText}>
            <div className={styles.groupName}>{group.groupName || "-"}</div>
            <div className={styles.groupMeta}>
              {users.length} {users.length === 1 ? "member" : "members"}
            </div>
          </div>
          <span className={styles.groupIcon}>
            {open ? <AiOutlineUp /> : <AiOutlineDown />}
          </span>
        </button>
        <button
          type="button"
          className={styles.groupEditButton}
          onClick={() => onEditGroup && onEditGroup(group)}
          aria-label="Edit group"
        >
          <FaPencilAlt />
        </button>
        <button
          type="button"
          className={styles.groupDeleteButton}
          onClick={() => setShowDeleteConfirm(true)}
          aria-label="Delete group"
        >
          <RiDeleteBin6Fill />
        </button>
      </div>

      {open && (
        <div className={styles.groupBody}>
          {users.length ? (
            <table className={usersStyles.usersTable}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Company Name</th>
                  <th>Email</th>
                  <th>Active Carts</th>
                  <th>Phone</th>
                  <th>Created At</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td
                      className={`${usersStyles.nameCell} ${styles.groupUserCell}`}
                    >
                      <span>{user?.name ?? "-"}</span>
                    </td>
                    <td className={styles.groupUserCell}>
                      {user?.companyName ?? "-"}
                    </td>
                    <td className={styles.groupUserCell}>
                      {user?.email ?? "-"}
                    </td>
                    <td className={styles.groupUserCell}>
                      {user?.cartsCount ?? user?.cartCount ?? 0}
                    </td>
                    <td className={styles.groupUserCell}>
                      {user?.phone || "-"}
                    </td>
                    <td className={styles.groupUserCell}>
                      {formatDate(user?.createdAt)}
                    </td>
                    <td className={usersStyles.viewDetailsCell}>
                      <button
                        type="button"
                        className={usersStyles.viewDetailsButton}
                        onClick={() => navigate(`/users/${user.id}/carts`)}
                      >
                        View Carts
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className={styles.groupNoUsers}>No users in this group</div>
          )}
        </div>
      )}

      <GroupDeleteConfirmation
        show={showDeleteConfirm}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={async () => {
          if (typeof onDeleteGroup === "function") {
            await onDeleteGroup(group._id);
          }
          setShowDeleteConfirm(false);
        }}
        groupName={group.groupName}
      />
    </div>
  );
};
